/**
 * CxL3 — transformers.
 *
 * **Energised only.** Ratio, winding resistance, insulation resistance, vector
 * group and polarity are all CxL2 dead-bus tests. They are the ones most often
 * pasted into a transformer script by habit, and every one of them asks an
 * engineer to work on something that is now live. They are not here.
 *
 * Draft defaults. The bands are the ones YET SB has run to; replace them with the
 * CxA's issued transformer script when it arrives, the way the LV catalogue was
 * checked against the MY02 script.
 */

import type { CatalogueTest } from '../catalogue.js';

export const CXL3_TRANSFORMER: readonly CatalogueTest[] = [
  {
    code: 'TX-VIS',
    name: 'Visual inspection and readiness',
    cxLevel: 'CxL3',
    equipmentTypes: ['Transformer'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Visual Inspection',
    checks: [
      { label: 'Rating plate legible and matches the approved schedule', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Tap position set as designed and recorded', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'HV danger signage, barriers and door interlocks in place', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Ventilation clearances free of storage and temporary works', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Earthing: tank/core, neutral and screen connections tight and labelled', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Cable terminations shrouded, no exposed live parts', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Oil level and absence of leaks (oil-filled units)', responseType: 'yes_no_applicable', toleranceMode: 'none' },
    ],
    shotCodes: ['TX-VIS-PLATE', 'TX-VIS-TAP', 'TX-VIS-EARTH'],
  },
  {
    code: 'TX-VT',
    name: 'No-load secondary voltage',
    cxLevel: 'CxL3',
    equipmentTypes: ['Transformer'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Voltage Test',
    note: 'Energised check against the tap in service. This is not a ratio test — that is CxL2.',
    checks: [
      { label: 'Secondary L1-L2', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Secondary L2-L3', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Secondary L3-L1', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Secondary L1-N', responseType: 'measured_pass_fail', unit: 'V', nominal: 240, tolerance: 10, toleranceMode: 'percent' },
      { label: 'Secondary L2-N', responseType: 'measured_pass_fail', unit: 'V', nominal: 240, tolerance: 10, toleranceMode: 'percent' },
      { label: 'Secondary L3-N', responseType: 'measured_pass_fail', unit: 'V', nominal: 240, tolerance: 10, toleranceMode: 'percent' },
      { label: 'Voltage unbalance between phases', responseType: 'measured_pass_fail', unit: '%', tolerance: 2, toleranceMode: 'max', acceptanceCriteria: '≤ 2%' },
    ],
    shotCodes: ['TX-VT-METER'],
  },
  {
    code: 'TX-PHASING',
    name: 'Phase rotation and phasing across the transformer',
    cxLevel: 'CxL3',
    equipmentTypes: ['Transformer'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Phase Rotation',
    note: 'Hosted on this report; the MV feeder and LV incomer are named as references, not copied in.',
    checks: [
      { label: 'Secondary phase rotation', responseType: 'pass_fail_na', toleranceMode: 'none', acceptanceCriteria: 'Clockwise / R-Y-B as designed', requiresPhoto: true },
      { label: 'Phasing agrees with the parallel source or alternate supply', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Phase colours and labelling consistent from primary to secondary', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['TX-PR-METER'],
  },
  {
    code: 'TX-TEMP',
    name: 'Temperature indication, alarm and trip',
    cxLevel: 'CxL3',
    equipmentTypes: ['Transformer'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    note: 'Simulated at the device, with the downstream effect observed — not just the lamp.',
    checks: [
      { label: 'Winding temperature indication reads and tracks', responseType: 'measured_pass_fail', unit: '°C', toleranceMode: 'none', requiresPhoto: true },
      { label: 'WTI alarm setpoint matches the approved schedule', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'WTI alarm simulated', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Alarm raised locally and at EPMS/BMS; no trip' },
      { label: 'WTI trip simulated', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Upstream and/or downstream breaker trips as designed' },
      { label: 'Oil temperature indication and alarm (oil-filled units)', responseType: 'yes_no_applicable', toleranceMode: 'none' },
    ],
    shotCodes: ['TX-TEMP-DISPLAY', 'TX-TEMP-ALARM', 'TX-TEMP-TRIP'],
  },
  {
    code: 'TX-COOLING',
    name: 'Cooling fans and auto operation',
    cxLevel: 'CxL3',
    equipmentTypes: ['Transformer'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    checks: [
      { label: 'Fans run in the correct direction', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Auto start at setpoint', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Fans start when the winding temperature reaches the start setpoint' },
      { label: 'Auto stop at setpoint', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Fans stop at the stop setpoint with no hunting' },
      { label: 'Fan failure alarm', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Alarm at panel and EPMS/BMS' },
      { label: 'Fan supply protection rated and labelled', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['TX-FAN-RUN', 'TX-FAN-ALARM'],
  },
  {
    code: 'TX-PROT',
    name: 'Protection trips to the connected breakers',
    cxLevel: 'CxL3',
    equipmentTypes: ['Transformer'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    note: 'The point is the breaker that opens, not the relay contact. Record which breaker, both sides.',
    checks: [
      { label: 'Buchholz alarm and trip (oil-filled units)', responseType: 'yes_no_applicable', toleranceMode: 'none' },
      { label: 'Pressure relief device trip (oil-filled units)', responseType: 'yes_no_applicable', toleranceMode: 'none' },
      { label: 'Transformer protection trips the HV breaker', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'HV breaker opens and indicates TRIP locally and at EPMS/BMS' },
      { label: 'Transformer protection trips the LV incomer', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'LV incomer opens where designed to' },
      { label: 'Trip circuit supervision healthy', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Neutral earthing connection continuous and, where fitted, NER in circuit', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: ['TX-PROT-HVTRIP', 'TX-PROT-LVTRIP', 'TX-PROT-NER'],
  },
  {
    code: 'TX-ALARM',
    name: 'EPMS / BMS alarm points',
    cxLevel: 'CxL3',
    equipmentTypes: ['Transformer'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Check with EPMS/BMS',
    checks: [
      { label: 'Winding temperature value present and matching local reading', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'WTI alarm point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'WTI trip point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Fan status and fan fail points', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Transformer trip point', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['TX-BMS-SCREEN'],
  },
  {
    code: 'TX-THERMO',
    name: 'Thermographic survey under load',
    cxLevel: 'CxL3',
    equipmentTypes: ['Transformer'],
    scopes: ['unit'],
    defaultSelected: false,
    section: 'Visual Inspection',
    note: 'Off by default: needs meaningful load, which is rarely available at startup.',
    checks: [
      { label: 'HV and LV terminations show no abnormal rise', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Load at time of survey recorded', responseType: 'measured_pass_fail', unit: '%', toleranceMode: 'none' },
    ],
    shotCodes: ['TX-THERMO-HV', 'TX-THERMO-LV'],
  },
];
